// @ts-nocheck
import React, { useEffect } from 'react';
import cn from 'classnames';
import { useAtomValue } from 'jotai';
import { dcbAtom, languageAtom } from '@shared/store/slice-atoms';
import { useParams } from 'react-router-dom';
import { DropdownButton, Dropdown } from 'react-bootstrap';
import { changeLanguage } from '@entities/language/store';
import { setUserSelectedLanguageManually, setCacheData } from '@entities/dcb';
import { useTranslation } from 'react-i18next';
import languageData from '@shared/config/constants/languages';
import { Helpers, setHtmlDirection } from '@shared/utils';
import styles from '@shared/ui/language-selector/styles/LanguageSelecor.module.css';
import { getDirFromLocale } from '@entities/language/model/utils';
import {
  isVariationV3Route,
  isVariationV4Route,
  isVariationV5Route,
} from '@shared/utils/general';
import {
  V5_CHECKMARK,
  V5_CHECKMARK_TRIAL,
  V5_DOWNLOAD_ICON,
  V5_DOWNLOAD_ICON_TRIAL,
} from '@shared/config/constants/api-sources';
import { isMY } from '@entities/country';
import { LogService } from '@shared/services/LogService';
import { ADDITIONAL_LANGUAGES, ALL_LANGUAGES, AR_lang, EN_lang } from '@entities/language';
import { isAg26MarketingVariation } from '@shared/ui/unlock-vuz-banner';

const getDropdownLanguages = (availableLanguages, allowedLanguages) => {
  if (!allowedLanguages.length) return availableLanguages;

  const byKey = new Map();
  [...ALL_LANGUAGES, ...ADDITIONAL_LANGUAGES, ...availableLanguages].forEach((item) => {
    byKey.set(String(item.lang).toLowerCase(), item);
  });

  return allowedLanguages.map((key) => byKey.get(String(key).toLowerCase())).filter(Boolean);
};

const LanguageSelector = ({
  languageConfig = {},
  isMin = false,
  uniqueApiSource = false,
  isUnsubPage = false,
  isSpecial = false,
}) => {
  const { t } = useTranslation();
  const { lang: langFromUrl } = useParams();
  const { i18n } = useTranslation();

  const { availableLanguages = languageData } = languageConfig;

  const languageConfigFromConfiguration = languageConfig?.availableLanguages || null;

  const language = useAtomValue(languageAtom);
  const dcb = useAtomValue(dcbAtom);
  const homeData = dcb.homeData;
  const dcbHomeReceived = dcb.dcbHomeReceived;
  const cacheData = dcb.cacheData;
  const isV3Route = isVariationV3Route(dcb.variationKey);
  const isV4Route = isVariationV4Route(dcb.variationKey);
  const isV5Route = isVariationV5Route(dcb.variationKey);

  const allowedLanguages = Array.isArray(homeData?.allowedLanguages)
    ? homeData.allowedLanguages
    : [];
    const hasAllowedLanguages = allowedLanguages.length > 0;
  const dropdownLanguages = getDropdownLanguages(availableLanguages, allowedLanguages);

  const handleChangeLanguage = ({ locale, displayName, direction }) => {
    let phoneTelInput = document.getElementById('phone-tel-input');
    if (phoneTelInput && phoneTelInput.value) {
      let phoneTelInputValue = phoneTelInput.value;
      setCacheData({ ...cacheData, phone: phoneTelInputValue });
    }
    i18n.changeLanguage(locale);
    changeLanguage({ locale, displayName, direction });

    const dir = getDirFromLocale(locale);
    setHtmlDirection(dir);

    setUserSelectedLanguageManually();
    if (!isUnsubPage) {
      LogService.languageChangedManually(locale);
    }
  };

  useEffect(() => {
    if (isV5Route || isSpecial) return;

    const explicitLang = langFromUrl || Helpers.getUrlParameter('lang') || null;

    if (explicitLang) {
      const supportedLanguage = [...ALL_LANGUAGES, ...ADDITIONAL_LANGUAGES].find(
        (item) => item.lang === explicitLang
      );

      if (supportedLanguage) {
        handleChangeLanguage({
          locale: supportedLanguage.lang,
          displayName: supportedLanguage.name,
          direction: supportedLanguage.direction,
        });
      }

      return;
    }

    if (languageConfigFromConfiguration && !hasAllowedLanguages) {
      const {
        lang: langLocale,
        name: displayName,
        direction,
      } = languageConfigFromConfiguration?.[0] || {};

      const firstLanguageFromConfiguration = {
        locale: langLocale || 'en',
        displayName: displayName || 'English',
        direction: direction || 'ltr',
      };

      handleChangeLanguage({ ...firstLanguageFromConfiguration });

      return;
    }

    let firstLanguage = 'en';
    if (navigator && navigator.languages && navigator.languages[0]) {
      firstLanguage = navigator.languages[0];
    }
    if (firstLanguage.includes('ar')) {
      handleChangeLanguage({ locale: 'ar', displayName: 'العربية', direction: 'rtl' });
    }

    if (firstLanguage.includes('kr')) {
      handleChangeLanguage({ locale: 'kr', displayName: 'کوردی', direction: 'rtl' });
    }
  }, []);

  useEffect(() => {
    const explicitLang = langFromUrl || Helpers.getUrlParameter('lang');
    if (explicitLang) return;

    if (!hasAllowedLanguages || dropdownLanguages.length === 0) return;
    const existsInAllowed = dropdownLanguages.some((item) => item.lang === language.locale);
    if (existsInAllowed) return;
    const firstAllowed = dropdownLanguages[0];
    handleChangeLanguage({
      locale: firstAllowed.lang,
      displayName: firstAllowed.name,
      direction: firstAllowed.direction,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAllowedLanguages, language.locale, dropdownLanguages.length]);

  const isLgAg26 = isAg26MarketingVariation();
  const ag26ToggleLanguages = dropdownLanguages.length > 0 ? dropdownLanguages : [EN_lang, AR_lang];
  const otherLanguage =
    ag26ToggleLanguages.find((item) => item.lang !== language.locale) ||
    (language.locale === 'ar' ? EN_lang : AR_lang);
  const otherLanguageName = otherLanguage?.name || language.displayName;

  const v3ClassName = isV3Route ? styles.v3_language_wrapper : '';
  const v3ItemClassName = isV3Route || isV5Route ? styles.v3_language_item : '';

  const v5ClassName = isV5Route ? styles.v5_language_wrapper : '';
  const v5ItemClassName = isV5Route ? styles.v5_language_item : '';

  const specialClassName = isSpecial ? styles.language_wrapper_special : '';
  const specialItemClassname = isSpecial ? styles.language_wrapper_special_item : '';
  const lgAg26ClassName = isLgAg26 ? styles.lg_ag26_language_wrapper : '';
  const lgAg26ItemClassName = isLgAg26 ? styles.lg_ag26_language_item : '';

  if (!dcbHomeReceived && !isUnsubPage && !isLgAg26)
    return <div className={styles.language_loading_text_contaienr}>{t('loading')}</div>;

  const getLanguageSVG = () => {
    if (isV3Route || isSpecial) {
      return (
        <div style={{ marginRight: '5px' }} tabIndex={1}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="17"
            height="17"
            viewBox="0 0 17 17"
            fill="none"
          >
            <path
              d="M8.49984 15.5833C7.53178 15.5833 6.61685 15.3972 5.75505 15.0251C4.89324 14.653 4.14052 14.1454 3.49688 13.5022C2.85371 12.859 2.34607 12.1066 1.97396 11.2448C1.60185 10.3829 1.41603 9.46801 1.4165 8.49996C1.4165 7.5201 1.60256 6.6021 1.97467 5.74596C2.34678 4.88982 2.85442 4.1404 3.49759 3.49771C4.14075 2.85407 4.893 2.34643 5.75434 1.97479C6.61567 1.60315 7.53084 1.4171 8.49984 1.41663C9.4797 1.41663 10.3977 1.60268 11.2538 1.97479C12.11 2.3469 12.8594 2.85454 13.5021 3.49771C14.1457 4.14088 14.6534 4.89053 15.025 5.74667C15.3966 6.60281 15.5827 7.52057 15.5832 8.49996C15.5832 9.46801 15.3971 10.3829 15.025 11.2448C14.6529 12.1066 14.1453 12.8593 13.5021 13.5029C12.8589 14.1461 12.1093 14.6537 11.2531 15.0258C10.397 15.3979 9.47923 15.5838 8.49984 15.5833ZM8.49984 14.1312C8.80678 13.7062 9.07241 13.2635 9.29671 12.8031C9.52102 12.3427 9.704 11.8527 9.84567 11.3333H7.154C7.29567 11.8527 7.47866 12.3427 7.70296 12.8031C7.92727 13.2635 8.19289 13.7062 8.49984 14.1312ZM6.65817 13.8479C6.44567 13.4583 6.25962 13.0538 6.10001 12.6345C5.94039 12.2152 5.8077 11.7814 5.70192 11.3333H3.61234C3.9547 11.9236 4.38277 12.4371 4.89655 12.8739C5.41032 13.3107 5.99753 13.6354 6.65817 13.8479ZM10.3415 13.8479C11.0026 13.6354 11.5901 13.3107 12.1038 12.8739C12.6176 12.4371 13.0454 11.9236 13.3873 11.3333H11.2978C11.1915 11.7819 11.0588 12.2159 10.8997 12.6352C10.7405 13.0545 10.5545 13.4588 10.3415 13.8479ZM3.01025 9.91663H5.41859C5.38317 9.68051 5.35649 9.44724 5.33855 9.21679C5.3206 8.98635 5.31187 8.7474 5.31234 8.49996C5.31234 8.25204 5.32131 8.0131 5.33925 7.78313C5.3572 7.55315 5.38364 7.31988 5.41859 7.08329H3.01025C2.95123 7.3194 2.90684 7.55268 2.87709 7.78313C2.84734 8.01357 2.8327 8.25251 2.83317 8.49996C2.83317 8.74788 2.84805 8.98682 2.8778 9.21679C2.90755 9.44676 2.9517 9.68004 3.01025 9.91663ZM6.83525 9.91663H10.1644C10.1998 9.68051 10.2265 9.44724 10.2445 9.21679C10.2624 8.98635 10.2711 8.7474 10.2707 8.49996C10.2707 8.25204 10.2617 8.0131 10.2438 7.78313C10.2258 7.55315 10.1994 7.31988 10.1644 7.08329H6.83525C6.79984 7.3194 6.77316 7.55268 6.75521 7.78313C6.73727 8.01357 6.72853 8.25251 6.729 8.49996C6.729 8.74788 6.73798 8.98682 6.75592 9.21679C6.77387 9.44676 6.80031 9.68004 6.83525 9.91663ZM11.5811 9.91663H13.9894C14.0484 9.68051 14.0928 9.44724 14.1226 9.21679C14.1523 8.98635 14.167 8.7474 14.1665 8.49996C14.1665 8.25204 14.1516 8.0131 14.1219 7.78313C14.0921 7.55315 14.048 7.31988 13.9894 7.08329H11.5811C11.6165 7.3194 11.6432 7.55268 11.6611 7.78313C11.6791 8.01357 11.6878 8.25251 11.6873 8.49996C11.6873 8.74788 11.6784 8.98682 11.6604 9.21679C11.6425 9.44676 11.616 9.68004 11.5811 9.91663ZM11.2978 5.66663H13.3873C13.045 5.07635 12.6171 4.56281 12.1038 4.126C11.5905 3.6892 11.0031 3.36454 10.3415 3.15204C10.554 3.54163 10.7401 3.94608 10.8997 4.36542C11.0593 4.78475 11.192 5.21849 11.2978 5.66663ZM7.154 5.66663H9.84567C9.704 5.14718 9.52102 4.65725 9.29671 4.19683C9.07241 3.73642 8.80678 3.29371 8.49984 2.86871C8.19289 3.29371 7.92727 3.73642 7.70296 4.19683C7.47866 4.65725 7.29567 5.14718 7.154 5.66663ZM3.61234 5.66663H5.70192C5.80817 5.21801 5.9411 4.78404 6.10071 4.36471C6.26032 3.94538 6.44614 3.54115 6.65817 3.15204C5.99706 3.36454 5.40962 3.6892 4.89584 4.126C4.38206 4.56281 3.95423 5.07635 3.61234 5.66663Z"
              fill="white"
            />
          </svg>
        </div>
      );
    }

    if (isV5Route || isLgAg26) {
      return (
        <div className={isLgAg26 ? styles.lg_ag26_globe : undefined} style={isLgAg26 ? undefined : { marginRight: '5px' }}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="17"
            height="17"
            viewBox="0 0 17 17"
            fill="none"
          >
            <path
              d="M8.50008 15.5833C7.53203 15.5833 6.6171 15.3972 5.75529 15.0251C4.89349 14.653 4.14076 14.1454 3.49712 13.5022C2.85396 12.859 2.34632 12.1066 1.97421 11.2448C1.6021 10.3829 1.41628 9.46801 1.41675 8.49996C1.41675 7.5201 1.6028 6.6021 1.97492 5.74596C2.34703 4.88982 2.85467 4.1404 3.49783 3.49771C4.141 2.85407 4.89325 2.34643 5.75458 1.97479C6.61592 1.60315 7.53108 1.4171 8.50008 1.41663C9.47994 1.41663 10.3979 1.60268 11.2541 1.97479C12.1102 2.3469 12.8596 2.85454 13.5023 3.49771C14.146 4.14088 14.6536 4.89053 15.0252 5.74667C15.3969 6.60281 15.5829 7.52057 15.5834 8.49996C15.5834 9.46801 15.3974 10.3829 15.0252 11.2448C14.6531 12.1066 14.1455 12.8593 13.5023 13.5029C12.8592 14.1461 12.1095 14.6537 11.2534 15.0258C10.3972 15.3979 9.47947 15.5838 8.50008 15.5833ZM8.50008 14.1312C8.80703 13.7062 9.07265 13.2635 9.29696 12.8031C9.52126 12.3427 9.70425 11.8527 9.84591 11.3333H7.15425C7.29592 11.8527 7.4789 12.3427 7.70321 12.8031C7.92751 13.2635 8.19314 13.7062 8.50008 14.1312ZM6.65842 13.8479C6.44592 13.4583 6.25986 13.0538 6.10025 12.6345C5.94064 12.2152 5.80794 11.7814 5.70217 11.3333H3.61258C3.95494 11.9236 4.38301 12.4371 4.89679 12.8739C5.41057 13.3107 5.99778 13.6354 6.65842 13.8479ZM10.3417 13.8479C11.0029 13.6354 11.5903 13.3107 12.1041 12.8739C12.6179 12.4371 13.0457 11.9236 13.3876 11.3333H11.298C11.1917 11.7819 11.0591 12.2159 10.8999 12.6352C10.7408 13.0545 10.5547 13.4588 10.3417 13.8479ZM3.0105 9.91663H5.41883C5.38342 9.68051 5.35674 9.44724 5.33879 9.21679C5.32085 8.98635 5.31211 8.7474 5.31258 8.49996C5.31258 8.25204 5.32155 8.0131 5.3395 7.78313C5.35744 7.55315 5.38389 7.31988 5.41883 7.08329H3.0105C2.95147 7.3194 2.90708 7.55268 2.87733 7.78313C2.84758 8.01357 2.83294 8.25251 2.83342 8.49996C2.83342 8.74788 2.84829 8.98682 2.87804 9.21679C2.90779 9.44676 2.95194 9.68004 3.0105 9.91663ZM6.8355 9.91663H10.1647C10.2001 9.68051 10.2268 9.44724 10.2447 9.21679C10.2627 8.98635 10.2714 8.7474 10.2709 8.49996C10.2709 8.25204 10.2619 8.0131 10.244 7.78313C10.2261 7.55315 10.1996 7.31988 10.1647 7.08329H6.8355C6.80008 7.3194 6.7734 7.55268 6.75546 7.78313C6.73751 8.01357 6.72878 8.25251 6.72925 8.49996C6.72925 8.74788 6.73822 8.98682 6.75617 9.21679C6.77411 9.44676 6.80055 9.68004 6.8355 9.91663ZM11.5813 9.91663H13.9897C14.0487 9.68051 14.0931 9.44724 14.1228 9.21679C14.1526 8.98635 14.1672 8.7474 14.1667 8.49996C14.1667 8.25204 14.1519 8.0131 14.1221 7.78313C14.0924 7.55315 14.0482 7.31988 13.9897 7.08329H11.5813C11.6167 7.3194 11.6434 7.55268 11.6614 7.78313C11.6793 8.01357 11.6881 8.25251 11.6876 8.49996C11.6876 8.74788 11.6786 8.98682 11.6607 9.21679C11.6427 9.44676 11.6163 9.68004 11.5813 9.91663ZM11.298 5.66663H13.3876C13.0452 5.07635 12.6174 4.56281 12.1041 4.126C11.5908 3.6892 11.0033 3.36454 10.3417 3.15204C10.5542 3.54163 10.7403 3.94608 10.8999 4.36542C11.0595 4.78475 11.1922 5.21849 11.298 5.66663ZM7.15425 5.66663H9.84591C9.70425 5.14718 9.52126 4.65725 9.29696 4.19683C9.07265 3.73642 8.80703 3.29371 8.50008 2.86871C8.19314 3.29371 7.92751 3.73642 7.70321 4.19683C7.4789 4.65725 7.29592 5.14718 7.15425 5.66663ZM3.61258 5.66663H5.70217C5.80842 5.21801 5.94135 4.78404 6.10096 4.36471C6.26057 3.94538 6.44639 3.54115 6.65842 3.15204C5.9973 3.36454 5.40986 3.6892 4.89608 4.126C4.3823 4.56281 3.95447 5.07635 3.61258 5.66663Z"
              fill="#3F3F3F"
            />
          </svg>
        </div>
      );
    }

    return null;
  };

  return (
    <div
      className={cn(styles['language-div'], isLgAg26 && styles.lg_ag26_language_div)}
      style={
        isLgAg26
          ? { padding: 0 }
          : uniqueApiSource
          ? { paddingTop: '10px' }
          : isV4Route
          ? { padding: '0' }
          : null
      }
    >
      {isMin ? (
        <div className="language-select">
          {dropdownLanguages.map((item) => (
            <span
              key={item.lang}
              className="language-min-item"
              onClick={() =>
                handleChangeLanguage({
                  locale: item.lang,
                  displayName: item.name,
                  direction: item.direction,
                })
              }
            >
              {String(item.lang).toUpperCase()}
            </span>
          ))}
        </div>
      ) : isLgAg26 ? (
        <div className={styles.lg_ag26_language_wrapper}>
          <button
            type="button"
            id="dropdown-btn-language"
            onClick={() =>
              handleChangeLanguage({
                locale: otherLanguage.lang,
                displayName: otherLanguage.name,
                direction: otherLanguage.direction,
              })
            }
          >
            {getLanguageSVG()}
            <span className={styles.v5_language_display_name}>{otherLanguageName}</span>
          </button>
        </div>
      ) : (
        <DropdownButton
          id="dropdown-btn-language"
          disabled={dropdownLanguages.length === 0}
          title={
            <>
              {getLanguageSVG()}
              <span
                className={
                  isV5Route ? styles.v5_language_display_name : styles.v3_language_display_name
                }
              >
                {isLgAg26 ? otherLanguageName : language.displayName}
              </span>
            </>
          }
          variant={'default'}
          className={lgAg26ClassName || v3ClassName || v5ClassName || specialClassName}
        >
          {dropdownLanguages.map((item) => {
            return (
              language.locale !== item.lang && (
                <Dropdown.Item
                  key={item.lang}
                  as="button"
                  onSelect={() =>
                    handleChangeLanguage({
                      locale: item.lang,
                      displayName: item.name,
                      direction: item.direction,
                    })
                  }
                  className={
                    lgAg26ItemClassName || v3ItemClassName || v5ItemClassName || specialItemClassname
                  }
                >
                  {item.name}
                </Dropdown.Item>
              )
            );
          })}

          {!hasAllowedLanguages &&
            ADDITIONAL_LANGUAGES &&
            !(
              isMY(homeData.country) &&
              (homeData.source === V5_DOWNLOAD_ICON ||
                homeData.source === V5_CHECKMARK ||
                homeData.source === V5_CHECKMARK_TRIAL ||
                homeData.source === V5_DOWNLOAD_ICON_TRIAL)
            ) &&
            ADDITIONAL_LANGUAGES.map((item) => {
              return (
                language.locale !== item.lang &&
                homeData &&
                homeData.country === item.country &&
                !dropdownLanguages?.find((aItem) => aItem?.lang === item?.lang) && (
                  <Dropdown.Item
                    key={item.lang}
                    as="button"
                    onSelect={() =>
                      handleChangeLanguage({
                        locale: item.lang,
                        displayName: item.name,
                        direction: item.direction,
                      })
                    }
                    className={v3ItemClassName}
                  >
                    {item.name}
                  </Dropdown.Item>
                )
              );
            })}
        </DropdownButton>
      )}
    </div>
  );
};

export { LanguageSelector };
